import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { PinPad } from "@/components/PinPad";
import { isValidPinFormat, savePin } from "@/lib/pin";

export function PinSetupWizard({ open, setOpen, configured, onSaved }) {
  const [step, setStep] = useState(0);
  const [pin, setPin] = useState("");
  const [firstPin, setFirstPin] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setPin("");
    setFirstPin("");
    setError("");
    setSaving(false);
  }, [open]);

  function handleOpenChange(nextOpen) {
    if (saving) return;
    setOpen(nextOpen);
  }

  function goBack() {
    if (saving) return;
    if (step === 1) {
      setStep(0);
      setPin("");
      setError("");
      return;
    }
    setOpen(false);
  }

  async function continueWizard() {
    if (!isValidPinFormat(pin)) {
      setError("El PIN debe tener entre 4 y 6 dígitos");
      return;
    }

    if (step === 0) {
      setFirstPin(pin);
      setPin("");
      setError("");
      setStep(1);
      return;
    }

    if (pin !== firstPin) {
      setError("Los PIN no coinciden");
      setPin("");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await savePin(pin);
      onSaved?.();
      setStep(2);
    } catch (saveError) {
      setError(saveError.message || "No se ha podido guardar el PIN");
    } finally {
      setSaving(false);
    }
  }

  const stepValid = isValidPinFormat(pin);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        showClose={false}
        className="flex h-dvh max-h-dvh w-screen flex-col overflow-hidden rounded-none p-0 sm:rounded-none"
      >
        <div className="flex min-h-0 flex-1 flex-col bg-neutral-100">
          <header className="flex h-[4.5rem] shrink-0 items-center justify-between border-b border-neutral-300 px-3 sm:px-6">
            <Button
              type="button"
              variant="ghost"
              onClick={goBack}
              disabled={saving}
              className="h-12 px-3 text-neutral-600"
            >
              <Icon icon="mdi:arrow-left" className="mr-2 text-xl" />
              Volver
            </Button>
            <span className="text-sm font-semibold text-neutral-700">
              Configurar PIN
            </span>
            <span className="w-12" />
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-8 sm:px-6 sm:py-12">
            <div className="mx-auto flex w-full max-w-xs flex-col items-center">
              <div className="mb-7 w-full">
                <div
                  className="flex gap-2"
                  aria-label={`Paso ${step + 1} de 3`}
                >
                  {[0, 1, 2].map((item) => (
                    <span
                      key={item}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        item <= step ? "bg-blue-600" : "bg-neutral-300"
                      }`}
                    />
                  ))}
                </div>
                <p className="mt-2 text-center text-xs text-neutral-400">
                  Paso {step + 1} de 3
                </p>
              </div>

              {step === 2 ? (
                <div className="w-full text-center">
                  <SheetTitle className="text-2xl font-semibold text-neutral-900">
                    {configured ? "PIN actualizado" : "PIN activado"}
                  </SheetTitle>
                  <p className="mx-auto mt-2 max-w-xs text-sm leading-6 text-neutral-500">
                    La protección de Tickets y Ajustes ya está activa en este
                    TPV.
                  </p>
                  <Button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="mt-8 h-14 w-full text-base"
                  >
                    Finalizar
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-center">
                    <SheetTitle className="text-xl font-semibold text-neutral-900">
                      {step === 0
                        ? configured
                          ? "Crea un nuevo PIN"
                          : "Crea tu PIN"
                        : "Repite el PIN"}
                    </SheetTitle>
                    <p className="mx-auto mt-2 max-w-xs text-sm leading-5 text-neutral-500">
                      {step === 0
                        ? "Elige entre 4 y 6 dígitos."
                        : "Introduce exactamente el mismo PIN."}
                    </p>
                  </div>

                  <div className="mt-7 w-full">
                    <PinPad
                      value={pin}
                      onChange={(value) => {
                        setError("");
                        setPin(value);
                      }}
                      maxLength={6}
                      disabled={saving}
                      loading={saving}
                      message={
                        error ||
                        (step === 0 ? "4–6 dígitos" : "Confirma el PIN")
                      }
                      error={Boolean(error)}
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={continueWizard}
                    disabled={!stepValid || saving}
                    className="mt-6 h-14 w-full text-base"
                  >
                    {saving ? "Guardando…" : "Continuar"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
